import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { Column, eColumnType, TableConfig } from './mot-table-config.interface';
import { MatTableDataSource } from '@angular/material/table';

@Component({
    selector: 'mot-table',
    templateUrl: './mot-table.component.html',
    styleUrls: ['./mot-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MotTableComponent<T> implements OnChanges, AfterViewInit, OnDestroy {
  @Input() class: string;
  /**
   * a11y: alt text for the empty-state illustration. Callers should pass a
   * localized string. Falls back to an English description if not provided
   * so the image is never announced as "image" with no context.
   */
  @Input() emptyAlt: string = 'No data to display';
  /** Data row index to scroll into view once the current dataSource has rendered. */
  @Input() scrollToIndex: number | null = null;
  columnTypes = eColumnType;

  @ViewChild('tableContainer') tableContainerRef?: ElementRef<HTMLElement>;
  private scrollVisibilityObserver?: ResizeObserver;

  _config: TableConfig<T>;
  _data: MatTableDataSource<T> = new MatTableDataSource<T>();
  displayedColumns: string[] = [];
  selectedRow: T;

  @Input() set config(val: TableConfig<T>) {
    this.displayedColumns = [
      ...this.displayedColumns,
      ...val.Columns.map((col) => col.columnId),
    ];

    this._config = val;
  }

  @Input() set dataSource(val: T[]) {
    this._data.data = val;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['scrollToIndex'] || changes['dataSource']) && this.scrollToIndex != null) {
      // Defer until Angular has rendered the rows for the current dataSource.
      setTimeout(() => this.scrollRowIntoView());
    }
  }

  ngAfterViewInit(): void {
    const container = this.tableContainerRef?.nativeElement;
    if (!container) {
      return;
    }

    // A parent mat-tab (or similar) toggles this container between zero and real
    // height as it activates/deactivates - re-scroll every time it becomes visible
    // again, not just the first time, so revisiting the tab keeps landing on the
    // target row instead of only working once.
    let wasVisible = container.clientHeight > 0;
    this.scrollVisibilityObserver = new ResizeObserver(() => {
      const isVisible = container.clientHeight > 0;
      if (isVisible && !wasVisible) {
        this.scrollRowIntoView();
      }
      wasVisible = isVisible;
    });
    this.scrollVisibilityObserver.observe(container);
  }

  private scrollRowIntoView(): void {
    const index = this.scrollToIndex;
    const container = this.tableContainerRef?.nativeElement;
    // A hidden (e.g. inactive mat-tab) container has zero height - scrollIntoView is a no-op then;
    // the visibility observer will retry once it's actually shown.
    if (index == null || !container || container.clientHeight === 0) {
      return;
    }
    const rows = container.querySelectorAll<HTMLElement>(
      'tr.mat-mdc-row:not(.no-data-row), tr.mat-row:not(.no-data-row)'
    );
    rows[index]?.scrollIntoView({ block: 'center' });
  }

  ngOnDestroy(): void {
    this.scrollVisibilityObserver?.disconnect();
  }

  get hasSingleRow(): boolean {
    return this._data?.data?.length === 1;
  }

  get hasMultipleRows(): boolean {
    return (this._data?.data?.length ?? 0) > 1;
  }

  getCellClass(element: T, column: Column<T>): string | string[] {
    if (column.cellClass) {
      if (
        typeof column.cellClass === 'string' ||
        Array.isArray(column.cellClass)
      ) {
        return column.cellClass;
      } else if (typeof column.cellClass === 'function') {
        return column.cellClass(element);
      }
    }

    return null;
  }

  getHeaderCellClass(column: Column<T>): string | string[] {
    if (column.headerClass) {
      if (
        typeof column.headerClass === 'string' ||
        Array.isArray(column.headerClass)
      ) {
        return column.headerClass;
      } else if (typeof column.headerClass === 'function') {
        return column.headerClass();
      }
    }

    return null;
  }

  iconAssetUrl(iconName: string | null): string {
    return `/assets/icons/${iconName}.svg`;
  }

  getCellStyles(
    element: T,
    column: Column<T>
  ): { [klass: string]: any } | { [klass: string]: any }[] {
    if (column.cellStyles) {
      if (
        typeof column.cellStyles === 'object' ||
        Array.isArray(column.cellStyles)
      ) {
        return column.cellStyles;
      } else if (typeof column.cellStyles === 'function') {
        return column.cellStyles(element);
      }
    }

    return null;
  }
}
